import { useEffect, useRef } from "react";

// ── inline GL helpers (no external deps) ──────────────────────────────────

function compileShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS))
    throw new Error(gl.getShaderInfoLog(shader) ?? "shader error");
  return shader;
}

class GLProgram {
  program: WebGLProgram;
  uniforms: Record<string, WebGLUniformLocation | null> = {};
  constructor(private gl: WebGLRenderingContext, vert: string, frag: string) {
    const p = gl.createProgram()!;
    gl.attachShader(p, compileShader(gl, gl.VERTEX_SHADER, vert));
    gl.attachShader(p, compileShader(gl, gl.FRAGMENT_SHADER, frag));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS))
      throw new Error(gl.getProgramInfoLog(p) ?? "link error");
    this.program = p;
    const n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS) as number;
    for (let i = 0; i < n; i++) {
      const name = gl.getActiveUniform(p, i)!.name;
      this.uniforms[name] = gl.getUniformLocation(p, name);
    }
  }
  bind() { this.gl.useProgram(this.program); }
}

// ── GLSL shaders ──────────────────────────────────────────────────────────

const VERT = `precision highp float;attribute vec2 aPosition;varying vec2 vUv;varying vec2 vL;varying vec2 vR;varying vec2 vT;varying vec2 vB;uniform vec2 texelSize;void main(){vUv=aPosition*0.5+0.5;vL=vUv-vec2(texelSize.x,0.0);vR=vUv+vec2(texelSize.x,0.0);vT=vUv+vec2(0.0,texelSize.y);vB=vUv-vec2(0.0,texelSize.y);gl_Position=vec4(aPosition,0.0,1.0);}`;
const CLEAR = `precision mediump float;precision mediump sampler2D;varying vec2 vUv;uniform sampler2D uTexture;uniform float value;void main(){gl_FragColor=value*texture2D(uTexture,vUv);}`;
const DISPLAY = `precision highp float;precision highp sampler2D;varying vec2 vUv;uniform sampler2D uTexture;void main(){vec3 c=texture2D(uTexture,vUv).rgb;float a=max(c.r,max(c.g,c.b));gl_FragColor=vec4(c,a);}`;
const SPLAT = `precision highp float;precision highp sampler2D;varying vec2 vUv;uniform sampler2D uTarget;uniform float aspectRatio;uniform vec3 color;uniform vec2 point;uniform float radius;void main(){vec2 p=vUv-point.xy;p.x*=aspectRatio;vec3 splat=exp(-dot(p,p)/radius)*color;vec3 base=texture2D(uTarget,vUv).xyz;gl_FragColor=vec4(base+splat,1.0);}`;
const ADVECTION = `precision highp float;precision highp sampler2D;varying vec2 vUv;uniform sampler2D uVelocity;uniform sampler2D uSource;uniform vec2 texelSize;uniform float dt;uniform float dissipation;void main(){vec2 coord=vUv-dt*texture2D(uVelocity,vUv).xy*texelSize;gl_FragColor=dissipation*texture2D(uSource,coord);gl_FragColor.a=1.0;}`;
const DIVERGENCE = `precision mediump float;precision mediump sampler2D;varying vec2 vUv;varying vec2 vL;varying vec2 vR;varying vec2 vT;varying vec2 vB;uniform sampler2D uVelocity;void main(){float L=texture2D(uVelocity,vL).x;float R=texture2D(uVelocity,vR).x;float T=texture2D(uVelocity,vT).y;float B=texture2D(uVelocity,vB).y;vec2 boundary=step(vec2(0.0),vUv)*(1.0-step(vec2(1.0),vUv));if(vL.x<0.0){L=-L;}if(vR.x>1.0){R=-R;}if(vT.y>1.0){T=-T;}if(vB.y<0.0){B=-B;}float div=0.5*(R-L+T-B);gl_FragColor=vec4(div,0.0,0.0,1.0);}`;
const CURL = `precision mediump float;precision mediump sampler2D;varying vec2 vUv;varying vec2 vL;varying vec2 vR;varying vec2 vT;varying vec2 vB;uniform sampler2D uVelocity;void main(){float L=texture2D(uVelocity,vL).y;float R=texture2D(uVelocity,vR).y;float T=texture2D(uVelocity,vT).x;float B=texture2D(uVelocity,vB).x;float vorticity=R-L-T+B;gl_FragColor=vec4(0.5*vorticity,0.0,0.0,1.0);}`;
const VORTICITY = `precision highp float;precision highp sampler2D;varying vec2 vUv;varying vec2 vL;varying vec2 vR;varying vec2 vT;varying vec2 vB;uniform sampler2D uVelocity;uniform sampler2D uCurl;uniform float curl;uniform float dt;void main(){float L=texture2D(uCurl,vL).x;float R=texture2D(uCurl,vR).x;float T=texture2D(uCurl,vT).x;float B=texture2D(uCurl,vB).x;float C=texture2D(uCurl,vUv).x;vec2 force=0.5*vec2(abs(T)-abs(B),abs(R)-abs(L));force/=length(force)+0.0001;force*=curl*C;force.y*=-1.0;vec2 vel=texture2D(uVelocity,vUv).xy;gl_FragColor=vec4(vel+force*dt,0.0,1.0);}`;
const PRESSURE = `precision mediump float;precision mediump sampler2D;varying vec2 vUv;varying vec2 vL;varying vec2 vR;varying vec2 vT;varying vec2 vB;uniform sampler2D uPressure;uniform sampler2D uDivergence;void main(){float L=texture2D(uPressure,vL).x;float R=texture2D(uPressure,vR).x;float T=texture2D(uPressure,vT).x;float B=texture2D(uPressure,vB).x;float C=texture2D(uDivergence,vUv).x;float pressure=(L+R+B+T-C)*0.25;gl_FragColor=vec4(pressure,0.0,0.0,1.0);}`;
const GRADIENT = `precision mediump float;precision mediump sampler2D;varying vec2 vUv;varying vec2 vL;varying vec2 vR;varying vec2 vT;varying vec2 vB;uniform sampler2D uPressure;uniform sampler2D uVelocity;void main(){float L=texture2D(uPressure,vL).x;float R=texture2D(uPressure,vR).x;float T=texture2D(uPressure,vT).x;float B=texture2D(uPressure,vB).x;vec2 velocity=texture2D(uVelocity,vUv).xy;velocity.xy-=vec2(R-L,T-B)*0.5;gl_FragColor=vec4(velocity,0.0,1.0);}`;

// ── fluid config ───────────────────────────────────────────────────────────

const CONFIG = {
  densityDissipation: 0.97,
  velocityDissipation: 0.98,
  pressureDissipation: 0.8,
  pressureIterations: 20,
  curl: 28,
  splatRadius: 0.002,
};

// ── component ─────────────────────────────────────────────────────────────

export function FluidCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // size canvas to section
    const section = canvas.parentElement!;
    const setSize = () => {
      canvas.width = section.clientWidth;
      canvas.height = section.clientHeight;
    };
    setSize();
    const ro = new ResizeObserver(setSize);
    ro.observe(section);

    // WebGL context
    const params = { alpha: true, depth: false, stencil: false, antialias: false };
    const gl = (canvas.getContext("webgl", params) || canvas.getContext("experimental-webgl", params)) as WebGLRenderingContext;
    if (!gl) return;

    const halfFloat = gl.getExtension("OES_texture_half_float");
    const linFilter = gl.getExtension("OES_texture_half_float_linear");
    if (!halfFloat) return;
    const HF = halfFloat.HALF_FLOAT_OES;
    const filter = linFilter ? gl.LINEAR : gl.NEAREST;

    gl.clearColor(0, 0, 0, 0);

    // programs
    const pClear = new GLProgram(gl, VERT, CLEAR);
    const pDisplay = new GLProgram(gl, VERT, DISPLAY);
    const pSplat = new GLProgram(gl, VERT, SPLAT);
    const pAdvection = new GLProgram(gl, VERT, ADVECTION);
    const pDivergence = new GLProgram(gl, VERT, DIVERGENCE);
    const pCurl = new GLProgram(gl, VERT, CURL);
    const pVorticity = new GLProgram(gl, VERT, VORTICITY);
    const pPressure = new GLProgram(gl, VERT, PRESSURE);
    const pGradient = new GLProgram(gl, VERT, GRADIENT);

    // quad
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1,-1,1,1,1,1,-1]), gl.STATIC_DRAW);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0,1,2,0,2,3]), gl.STATIC_DRAW);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(0);

    // FBO helpers
    type FBO = [WebGLTexture, WebGLFramebuffer, number];
    const createFBO = (id: number, w: number, h: number): FBO => {
      gl.activeTexture(gl.TEXTURE0 + id);
      const tex = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, HF, null);
      const fbo = gl.createFramebuffer()!;
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
      gl.viewport(0, 0, w, h);
      gl.clear(gl.COLOR_BUFFER_BIT);
      return [tex, fbo, id];
    };

    const createDoubleFBO = (id: number, w: number, h: number) => {
      let a = createFBO(id, w, h), b = createFBO(id+1, w, h);
      return { get read(){ return a; }, get write(){ return b; }, swap(){ [a,b]=[b,a]; } };
    };

    const W = canvas.width >> 1, H = canvas.height >> 1;
    const density = createDoubleFBO(2, W, H);
    const velocity = createDoubleFBO(0, W, H);
    const divergence = createFBO(4, W, H);
    const curl = createFBO(5, W, H);
    const pressure = createDoubleFBO(6, W, H);

    const blit = (dest: WebGLFramebuffer | null) => {
      gl.bindFramebuffer(gl.FRAMEBUFFER, dest);
      gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
    };

    const splat = (x: number, y: number, dx: number, dy: number, color: [number,number,number]) => {
      pSplat.bind();
      gl.uniform1i(pSplat.uniforms.uTarget, velocity.read[2]);
      gl.uniform1f(pSplat.uniforms.aspectRatio, canvas.width / canvas.height);
      gl.uniform2f(pSplat.uniforms.point, x / canvas.width, 1 - y / canvas.height);
      gl.uniform3f(pSplat.uniforms.color, dx, -dy, 1);
      gl.uniform1f(pSplat.uniforms.radius, CONFIG.splatRadius);
      blit(velocity.write[1]);
      velocity.swap();

      gl.uniform1i(pSplat.uniforms.uTarget, density.read[2]);
      gl.uniform3f(pSplat.uniforms.color, color[0]*0.3, color[1]*0.3, color[2]*0.3);
      blit(density.write[1]);
      density.swap();
    };

    // 3 brand colors — fluid mixing creates natural gradient blending
    const PALETTE: [number,number,number][] = [
      [0.953, 0.835, 0.451],  // #F3D573 gold
      [0.325, 0.643, 0.812],  // #53A4CF blue
      [0.855, 0.357, 0.608],  // #DA5B9B pink
    ];
    let ci = 0;
    let splatCount = 0;
    const nextCol = (): [number,number,number] => {
      const c = PALETTE[ci % PALETTE.length];
      return [c[0] * 10, c[1] * 10, c[2] * 10];
    };

    // initial splats — spread evenly across the canvas, interleaving all 3 colors
    for (let i = 0; i < 18; i++) {
      ci = i;
      splat(
        (i / 18) * canvas.width + (Math.random() - 0.5) * canvas.width * 0.4,
        Math.random() * canvas.height,
        (Math.random() - 0.5) * 600,
        (Math.random() - 0.5) * 600,
        nextCol()
      );
    }
    ci = 0;

    // mouse / touch tracking
    const ptr = { x: canvas.width / 2, y: canvas.height / 2, dx: 0, dy: 0, down: false, moved: false, color: nextCol() };

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const nx = e.clientX - rect.left;
      const ny = e.clientY - rect.top;
      ptr.dx = (nx - ptr.x) * 8;
      ptr.dy = (ny - ptr.y) * 8;
      ptr.x = nx; ptr.y = ny;
      ptr.moved = true;
      ptr.down = true;
    };
    const onUp = () => { ptr.down = false; };
    section.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    // auto-animation for mobile: Lissajous path
    const isMobile = window.matchMedia("(pointer: coarse)").matches;

    // On touch devices skip touch listeners — auto-animation drives the fluid
    // and we must not call e.preventDefault() which would block native scroll.
    if (!isMobile) {
      const onTouch = (e: TouchEvent) => {
        const rect = canvas.getBoundingClientRect();
        const t = e.touches[0];
        const nx = t.clientX - rect.left;
        const ny = t.clientY - rect.top;
        ptr.dx = (nx - ptr.x) * 8;
        ptr.dy = (ny - ptr.y) * 8;
        ptr.x = nx; ptr.y = ny;
        ptr.moved = true;
        ptr.down = true;
      };
      section.addEventListener("touchmove", onTouch, { passive: true });
      section.addEventListener("touchstart", onTouch, { passive: true });
    }
    let autoT = 0;

    let lastTime = Date.now();
    let rafId = 0;

    const update = () => {
      const dt = Math.min((Date.now() - lastTime) / 1000, 0.016);
      lastTime = Date.now();
      const iW = 1 / W, iH = 1 / H;

      gl.viewport(0, 0, W, H);

      // on mobile, auto-drive the pointer along a Lissajous curve
      if (isMobile) {
        autoT += dt * 0.4;
        const nx = canvas.width  * (0.5 + 0.4 * Math.sin(autoT * 1.3));
        const ny = canvas.height * (0.5 + 0.4 * Math.sin(autoT * 1.7 + 1.0));
        ptr.dx = (nx - ptr.x) * 8;
        ptr.dy = (ny - ptr.y) * 8;
        ptr.x = nx; ptr.y = ny;
        ptr.moved = true;
      }

      if (ptr.moved) {
        splat(ptr.x, ptr.y, ptr.dx, ptr.dy, ptr.color);
        ptr.moved = false;
        splatCount++;
        if (splatCount % 60 === 0) {
          ci++;
          ptr.color = nextCol();
        }
      }

      // advect velocity
      pAdvection.bind();
      gl.uniform2f(pAdvection.uniforms.texelSize, iW, iH);
      gl.uniform1i(pAdvection.uniforms.uVelocity, velocity.read[2]);
      gl.uniform1i(pAdvection.uniforms.uSource, velocity.read[2]);
      gl.uniform1f(pAdvection.uniforms.dt, dt);
      gl.uniform1f(pAdvection.uniforms.dissipation, CONFIG.velocityDissipation);
      blit(velocity.write[1]); velocity.swap();

      // advect density
      gl.uniform1i(pAdvection.uniforms.uVelocity, velocity.read[2]);
      gl.uniform1i(pAdvection.uniforms.uSource, density.read[2]);
      gl.uniform1f(pAdvection.uniforms.dissipation, CONFIG.densityDissipation);
      blit(density.write[1]); density.swap();

      // curl
      pCurl.bind();
      gl.uniform2f(pCurl.uniforms.texelSize, iW, iH);
      gl.uniform1i(pCurl.uniforms.uVelocity, velocity.read[2]);
      blit(curl[1]);

      // vorticity
      pVorticity.bind();
      gl.uniform2f(pVorticity.uniforms.texelSize, iW, iH);
      gl.uniform1i(pVorticity.uniforms.uVelocity, velocity.read[2]);
      gl.uniform1i(pVorticity.uniforms.uCurl, curl[2]);
      gl.uniform1f(pVorticity.uniforms.curl, CONFIG.curl);
      gl.uniform1f(pVorticity.uniforms.dt, dt);
      blit(velocity.write[1]); velocity.swap();

      // divergence
      pDivergence.bind();
      gl.uniform2f(pDivergence.uniforms.texelSize, iW, iH);
      gl.uniform1i(pDivergence.uniforms.uVelocity, velocity.read[2]);
      blit(divergence[1]);

      // pressure clear
      pClear.bind();
      gl.activeTexture(gl.TEXTURE0 + pressure.read[2]);
      gl.bindTexture(gl.TEXTURE_2D, pressure.read[0]);
      gl.uniform1i(pClear.uniforms.uTexture, pressure.read[2]);
      gl.uniform1f(pClear.uniforms.value, CONFIG.pressureDissipation);
      blit(pressure.write[1]); pressure.swap();

      // pressure solve
      pPressure.bind();
      gl.uniform2f(pPressure.uniforms.texelSize, iW, iH);
      gl.uniform1i(pPressure.uniforms.uDivergence, divergence[2]);
      for (let i = 0; i < CONFIG.pressureIterations; i++) {
        gl.activeTexture(gl.TEXTURE0 + pressure.read[2]);
        gl.bindTexture(gl.TEXTURE_2D, pressure.read[0]);
        gl.uniform1i(pPressure.uniforms.uPressure, pressure.read[2]);
        blit(pressure.write[1]); pressure.swap();
      }

      // gradient subtract
      pGradient.bind();
      gl.uniform2f(pGradient.uniforms.texelSize, iW, iH);
      gl.uniform1i(pGradient.uniforms.uPressure, pressure.read[2]);
      gl.uniform1i(pGradient.uniforms.uVelocity, velocity.read[2]);
      blit(velocity.write[1]); velocity.swap();

      // display
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
      pDisplay.bind();
      gl.uniform1i(pDisplay.uniforms.uTexture, density.read[2]);
      blit(null);

      rafId = requestAnimationFrame(update);
    };

    rafId = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      section.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ mixBlendMode: "multiply" }}
    />
  );
}
